import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import chunk from "lodash/chunk";
import classNames from "classnames";
import { useMediaQuery } from "@mui/material";
import { Carousel, type CarouselRefAttributes } from "../../../common/components/carousel/Carousel";
import throttle from "lodash/throttle";
import { MEDIA_QUERY_MIN_TABLET } from "../../../utils/screenSizes";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";

export enum DesktopContainerTypes {
  DEFAULT = "DEFAULT",
  CAROUSEL = "CAROUSEL",
}

export enum MobileContainerTypes {
  DEFAULT = "DEFAULT",
  HORIZONTAL = "HORIZONTAL_SCROLL",
  CAROUSEL = "CAROUSEL",
}

export interface CMSTeasersProps<T extends { index: number }> {
  teasers: T[];
  scrollType?: DesktopContainerTypes;
  itemsPerRow?: number;
  mobileScrollType?: MobileContainerTypes;
  mobileItemsPerRow?: number;
  className?: string;
  renderTeaserDetailsContainer: (validIds: number[]) => ReactNode;
  renderTeaser: (teaser: T) => ReactNode;
}

export function CMSTeaserContainer<T extends { index: number }>({
  scrollType: desktopScrollType = DesktopContainerTypes.DEFAULT,
  itemsPerRow: desktopItemsPerRow = 1,
  mobileScrollType = MobileContainerTypes.DEFAULT,
  mobileItemsPerRow = 1,
  teasers,
  className,
  renderTeaserDetailsContainer,
  renderTeaser,
}: CMSTeasersProps<T>) {
  const isDesktop = useIsMinDesktopSize();

  const teasersWithId = teasers.map((teaser, index) => ({ ...teaser, id: index }));

  const containerRef = useRef<HTMLDivElement>(null);
  const isTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  const [maxTeasersPerRow, setMaxTeasersPerRow] = useState(isDesktop ? desktopItemsPerRow : mobileItemsPerRow);

  useEffect(() => {
    const fn = throttle(() => {
      if (isDesktop) {
        setMaxTeasersPerRow(desktopItemsPerRow);
      } else if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width === 0) {
          requestAnimationFrame(fn);
          return;
        }
        const calculatedTeasersPerRow = Math.floor(width / 228);
        setMaxTeasersPerRow(
          Math.max(calculatedTeasersPerRow > desktopItemsPerRow ? desktopItemsPerRow : calculatedTeasersPerRow, 1),
        );
      } else {
        setMaxTeasersPerRow((prev) => (prev && prev > 0 ? prev : 1));
      }
    }, 100);
    window.addEventListener("resize", fn);
    window.addEventListener("orientationchange", fn);
    fn();
    return () => {
      window.removeEventListener("resize", fn);
      window.removeEventListener("orientationchange", fn);
    };
  }, [isDesktop, isTablet, desktopItemsPerRow]);

  let itemStyle: CSSProperties | undefined = undefined;

  // Deprecated ?
  const scrollType: string = isDesktop ? desktopScrollType : mobileScrollType;

  if (scrollType !== MobileContainerTypes.HORIZONTAL) {
    itemStyle = {
      width: `${100 / maxTeasersPerRow}%`,
    };
  }

  const useCarousel =
    (isDesktop && desktopScrollType === DesktopContainerTypes.CAROUSEL) ||
    (!isDesktop && mobileScrollType === MobileContainerTypes.CAROUSEL);

  const containerCls = `scha__teasers__elements scha__teasers__elements--${scrollType}`;
  const elementCls = `scha__teasers__element scha__teasers__element--${scrollType}`;

  const carouselRef = useRef<CarouselRefAttributes>(null);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.setActivePage(0);
    }
  }, [teasers]);

  let data;
  if (useCarousel) {
    data = (
      <Carousel
        data={teasersWithId}
        pageItemsCount={maxTeasersPerRow}
        renderSlide={(data) => (
          <ul className={containerCls}>
            {data.data.map((item) => (
              <li key={`CMSTeaser-${item.index}`} style={itemStyle} className={elementCls}>
                {renderTeaser(item)}
              </li>
            ))}
          </ul>
        )}
        ref={carouselRef}
      />
    );
  } else {
    let rows;
    if (scrollType === MobileContainerTypes.HORIZONTAL) {
      rows = [teasersWithId];
    } else {
      rows = chunk(teasersWithId, maxTeasersPerRow);
    }
    data = (
      <>
        {rows.map((row, containerIndex) => (
          <div className={"scha__teasers__group"} key={`scha__teasers__level--${containerIndex}`}>
            <ul className={containerCls}>
              {row.map((item) => (
                <li key={item.id} style={itemStyle} className={elementCls}>
                  {renderTeaser(item)}
                </li>
              ))}
            </ul>
            {renderTeaserDetailsContainer(row.map((t) => t.index))}
          </div>
        ))}
      </>
    );
  }

  if (!teasers.length) {
    return null;
  }

  return (
    <div
      className={classNames(
        `scha__teasers scha__teasers--${scrollType}`,
        {
          "scha__teasers--isDesktop": isDesktop,
          "scha__teasers--isTablet": isTablet,
        },
        className,
      )}
      ref={containerRef}
    >
      {data}
    </div>
  );
}
